import { Component, computed, effect, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { baseCustomPanelStructureComponent } from '../../new-custom-panel/baseCustomPanelStructure';
import { CodeMirrorEditorComponent } from '../../../common/code-mirror-editor/code-mirror-editor.component';
import { WidgetService } from '../../../services/builder/widget.service';
import { FileDialogCustomization } from './filedialog-edit';
import { UniqueIdService } from '../../../services/builder/unique-id.service';
import { TextareaModule } from 'primeng/textarea';

@Component({
  selector: 'lib-filedialog-custom-panel',
  template: `
    <lib-base-cpanel
      [content]="content"
      [hideAutocompletion]="true"
      [form]="customizationForm"
      header="Personalizzazione Allega File"
      (closed)="updateWidget($event)"
    />
    <ng-template #content>
      @if (customizationForm) {
        <div [formGroup]="customizationForm" class="flex flex-col gap-2">
          <div class="flex w-full flex-col">
            <label for="desc">Descrizione</label>
            <textarea
              id="desc"
              pTextarea
              formControlName="description"
            ></textarea>
          </div>

          <div class="flex w-full flex-col">
            <label for="desc">Nome del file</label>
            <lib-code-mirror-editor
              [data]="customKeys()"
              formControlName="fileNameConfiguration"
            ></lib-code-mirror-editor>
            @let fileNameConfiguration =
              customizationForm.controls.fileNameConfiguration;
            @if (fileNameConfiguration.invalid) {
              @let error = fileNameConfiguration.errors?.['idNotExists'];
              <ul class="text-red-500">
                @if (error) {
                  @for (id of error.value; track $index) {
                    <li>L'id '{{ id }}' non esiste nel modulo</li>
                  }
                }
              </ul>
            }
          </div>

          <div class="flex w-full flex-row">
            <label for="required" class="w-full self-start">
              Campo obbligatorio
            </label>
            <p-checkbox
              id="required"
              formControlName="required"
              [binary]="true"
              class="self-end"
            ></p-checkbox>
          </div>
        </div>
      }
    </ng-template>
  `,
  imports: [
    ReactiveFormsModule,
    baseCustomPanelStructureComponent,
    InputTextModule,
    MatButtonModule,
    CheckboxModule,
    CodeMirrorEditorComponent,
    TextareaModule,
  ],
})
export class FileDialogCPanelComponent {
  private fb = inject(FormBuilder);
  protected widgetService = inject(WidgetService<FileDialogCustomization>);
  protected uniqueIdService = inject(UniqueIdService);

  protected customizationForm = this.fb.group({
    id: [
      '',
      [
        Validators.required,
        this.uniqueIdService.forbiddenIdValidator(
          this.widgetService.uniqueId ?? '',
        ),
        this.uniqueIdService.isEmptySpace(),
      ],
    ],
    description: [''],
    fileNameConfiguration: ['', [this.uniqueIdService.idNotExistsValidator()]],
    required: [false],
    showIfRule: ['', [this.uniqueIdService.idNotExistsValidator()]],
  });

  customKeys = computed(() => {
    const data = this.uniqueIdService.usedIds();

    return {
      ...data,
      $ModuleName$: '',
      $ModuleDate$: '',
    };
  });

  constructor() {
    effect(() => {
      const widget = this.widgetService.widget();
      if (!widget) return;

      const uniqueId = this.widgetService.uniqueId;
      if (!uniqueId) return;

      this.customizationForm.patchValue({
        id: uniqueId,
        description: widget.customization.description,
        fileNameConfiguration: widget.customization.fileNameConfiguration,
        required: widget.customization.required,
        showIfRule: widget.customization.showIfRule,
      });
    });
  }

  updateWidget(event: boolean) {
    if (!event) {
      this.widgetService.settingVisibility.set(false);
      return;
    }

    const formValue = this.customizationForm.value;
    const id = formValue.id?.trim();
    if (!id) return;

    this.widgetService.updateWidget(id, {
      description: formValue.description,
      fileNameConfiguration: formValue.fileNameConfiguration,
      required: formValue.required,
      showIfRule: formValue.showIfRule || '',
    });

    this.widgetService.settingVisibility.set(false);
  }
}
